import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal, X } from 'lucide-react';

import JobHeaderSearch from './JobHeaderSearch';
import JobHeaderDDJobtypes from './JobHeaderDDJobtypes';
import JobHeaderDDSalary from './JobHeaderDDSalary';
import JobHeaderExperience from './JobHeaderExperience';
import JobHeaderLocationFilter from './JobHeaderLocationFilter';
import JobHeaderScoreFilter from './JobHeaderScoreFilter';

import BagIcon from '../../../assets/icons/bag-icon.png';

const JobHeader = ({
    query, setQuery,
    selectedJobTypes, setSelectedJobTypes,
    minSalary, maxSalary, setMinSalary, setMaxSalary,
    locationQuery, setLocationQuery,
    selectedExperience, setSelectedExperience,
    selectedCities, setSelectedCities, cityOptions,
    selectedScoreRanges = [], setSelectedScoreRanges,
    clearFilters,
    sortBy, setSortBy,
}) => {
    const [showModal, setShowModal] = useState(false);

    const jobtypes = [
        { value: 'fulltime', label: 'Full-Time' },
        { value: 'parttime', label: 'Part-Time' },
        { value: 'remote', label: 'Remote' },
        { value: 'freelance', label: 'Freelance' },
        { value: 'contractor', label: 'Contract' },
    ];

    const activeFiltersCount = [
        selectedJobTypes.length > 0,
        minSalary > 0 || maxSalary < 300000,
        selectedExperience.length > 0,
        selectedCities.length > 0,
        selectedScoreRanges.length > 0,
    ].filter(Boolean).length;

    const modal = showModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => setShowModal(false)}
            />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-2">
                        <SlidersHorizontal className="w-4 h-4 text-[#3F57E0]" />
                        <span className="text-[15px] font-semibold text-[#0f172a]">Filters</span>
                        {activeFiltersCount > 0 && (
                            <span className="bg-[#3F57E0] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center leading-none">
                                {activeFiltersCount}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() => setShowModal(false)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex flex-col gap-6 px-6 py-5 overflow-y-auto">
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Salary Range</p>
                        <JobHeaderDDSalary
                            minSalary={minSalary}
                            maxSalary={maxSalary}
                            setMinSalary={setMinSalary}
                            setMaxSalary={setMaxSalary}
                        />
                    </div>

                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Job Type</p>
                        <JobHeaderDDJobtypes
                            items={jobtypes}
                            label="Job Type"
                            icon={BagIcon}
                            selectedOptions={selectedJobTypes}
                            setSelectedOptions={setSelectedJobTypes}
                        />
                    </div>

                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Location</p>
                        <JobHeaderLocationFilter
                            selectedCities={selectedCities}
                            setSelectedCities={setSelectedCities}
                            cityOptions={cityOptions}
                        />
                    </div>

                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Experience Level</p>
                        <JobHeaderExperience
                            selectedExperience={selectedExperience}
                            setSelectedExperience={setSelectedExperience}
                        />
                    </div>

                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Match Score</p>
                        <JobHeaderScoreFilter
                            selectedScoreRanges={selectedScoreRanges}
                            setSelectedScoreRanges={setSelectedScoreRanges}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0 rounded-b-2xl">
                    <button
                        onClick={clearFilters}
                        disabled={activeFiltersCount === 0}
                        className={`text-sm font-medium transition-colors ${
                            activeFiltersCount === 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-600 hover:text-[#3F57E0]'
                        }`}
                    >
                        Clear all
                    </button>
                    <button
                        onClick={() => setShowModal(false)}
                        className="px-5 py-2 bg-[#3F57E0] hover:bg-[#3347c7] text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                        Apply filters
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );

    return (
        <div className="w-full">
            <div className="flex items-center gap-2">
                <JobHeaderSearch query={query} setQuery={setQuery} />

                <button
                    onClick={() => setShowModal(true)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all flex-shrink-0 ${
                        activeFiltersCount > 0
                            ? 'bg-[#EEF1FE] border-[#3F57E0] text-[#3F57E0]'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <SlidersHorizontal className="w-4 h-4" />
                    <span>Filters</span>
                    {activeFiltersCount > 0 && (
                        <span className="bg-[#3F57E0] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center leading-none">
                            {activeFiltersCount}
                        </span>
                    )}
                </button>

                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 outline-none hover:border-gray-300 cursor-pointer flex-shrink-0"
                >
                    <option value="date">Sort: Recency</option>
                    <option value="score">Sort: Match score</option>
                </select>
            </div>

            {modal}
        </div>
    );
};

export default JobHeader;
